import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';

import useComponentMounted from '../../../hooks/useComponentMounted';
import UserContext from '../../../context/UserContext';
import Lead from '../../Model/Lead';
import LeadConversation, { CONVERSATIONS_PAGE_SIZE } from '../../Model/LeadConversation';
import LeadConversationsContext, {
  initialValues,
  ConversationBucket,
  ConversationChannel,
} from './LeadConversationsContext';

const SEARCH_FIELDS = ['leadName', 'leadEmail', 'leadPhoneNumber'];

const LeadConversationsContextProvider = ({
  children,
}) => {
  const [isReady, setIsReady] = useState(initialValues.isReady);
  const [conversationCollectionList, setConversationCollectionList] = useState([]);
  const [conversationList, setConversationList] = useState(initialValues.conversationList);
  const [isLoadingConversations, setIsLoadingConversations] = useState(
    initialValues.isLoadingConversations,
  );
  const [hasMoreConversations, setHasMoreConversations] = useState(
    initialValues.hasMoreConversations,
  );
  const [selectedBucket, setSelectedBucket] = useState(initialValues.selectedBucket);
  const [activeLeadId, setActiveLeadId] = useState(initialValues.activeLeadId);
  const [activeLeadDoc, setActiveLeadDoc] = useState(initialValues.activeLeadDoc);
  const [isLoading, setIsLoading] = useState(initialValues.isLoading);
  const [isPendingTakeover, setIsPendingTakeover] = useState(initialValues.isPendingTakeover);
  const [pendingClaimedByUsername, setPendingClaimedByUsername] = useState(
    initialValues.pendingClaimedByUsername,
  );
  const [pendingTakeoverLeadId, setPendingTakeoverLeadId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChannel, setSelectedChannel] = useState(initialValues.selectedChannel);
  const [activeChannel, setActiveChannel] = useState(initialValues.activeChannel);
  const [igConversationCollectionList, setIGConversationCollectionList] = useState([]);
  const [igConversationList, setIGConversationList] = useState(initialValues.igConversationList);
  const [isLoadingIGConversations, setIsLoadingIGConversations] = useState(
    initialValues.isLoadingIGConversations,
  );
  const [hasMoreIGConversations, setHasMoreIGConversations] = useState(
    initialValues.hasMoreIGConversations,
  );
  const prevActiveLeadIdRef = useRef('');
  const conversationByLeadIdRef = useRef(new Map());
  const isComponentMountedRef = useComponentMounted();
  const { state: locationState } = useLocation();
  const initialLeadIdRef = useRef(locationState?.initialLeadId || '');

  const {
    userDoc: { id: authUserId, name: authUserName },
  } = useContext(UserContext);

  useEffect(() => {
    const init = async () => {
      const isArchived = selectedBucket === ConversationBucket.ARCHIVED;
      const initialCollection = LeadConversation.getConversations(isArchived, {
        limit: CONVERSATIONS_PAGE_SIZE,
      });

      await initialCollection.fetch();

      if (isComponentMountedRef.current) {
        setConversationCollectionList([initialCollection]);
        setHasMoreConversations(initialCollection.docs.length === CONVERSATIONS_PAGE_SIZE);
        setIsReady(true);

        if (initialLeadIdRef.current && isComponentMountedRef.current) {
          setActiveLeadId(initialLeadIdRef.current);
        } else {
          // Auto-select the first unclaimed (or expired claim) conversation
          const firstUnclaimed = initialCollection.docs.find(
            (doc) => !doc.claimedBy || !doc.isClaimValid,
          );
          if (firstUnclaimed && isComponentMountedRef.current) {
            setActiveLeadId(firstUnclaimed.leadId);
          }
        }
      }
    };

    if (!isReady && selectedChannel === ConversationChannel.TEXT_EMAIL) {
      init();
    }
  }, [
    isReady,
    selectedBucket,
    selectedChannel,
    isComponentMountedRef,
  ]);

  useEffect(() => {
    setIsReady(false);
    setActiveLeadId('');
    setActiveLeadDoc(null);
    setSearchQuery('');
  }, [selectedBucket]);

  useEffect(() => {
    setIsReady(false);
    setActiveLeadId('');
    setActiveChannel(ConversationChannel.TEXT_EMAIL);
    setSearchQuery('');
    setIGConversationCollectionList([]);
  }, [selectedChannel]);

  useEffect(() => {
    const initIG = async () => {
      const igIsArchived = selectedBucket === ConversationBucket.ARCHIVED;
      const initialIGCollection = LeadConversation.getIGConversations(igIsArchived, {
        limit: CONVERSATIONS_PAGE_SIZE,
      });

      await initialIGCollection.fetch();

      if (isComponentMountedRef.current) {
        setIGConversationCollectionList([initialIGCollection]);
        setHasMoreIGConversations(initialIGCollection.docs.length === CONVERSATIONS_PAGE_SIZE);
        setIsReady(true);
      }
    };

    if (!isReady && selectedChannel === ConversationChannel.INSTAGRAM) {
      initIG();
    }
  }, [isReady, selectedBucket, selectedChannel, isComponentMountedRef]);

  useEffect(() => {
    let isCurrentRequest = true;

    setIsLoading(true);
    const fetchLeadDoc = async () => {
      if (!activeLeadId) {
        setActiveLeadDoc(null);
        setIsLoading(false);
        return;
      }

      try {
        const leadDoc = await Lead.getLeadById(activeLeadId);

        if (isComponentMountedRef.current && isCurrentRequest) {
          setActiveLeadDoc(leadDoc);
        }
      } finally {
        if (isComponentMountedRef.current && isCurrentRequest) {
          setIsLoading(false);
        }
      }
    };

    fetchLeadDoc();

    return () => {
      isCurrentRequest = false;
    };
  }, [activeLeadId, isComponentMountedRef]);

  /**
   * Get the conversation document for the given lead id.
   * We use this to avoid fetching existing documents,
   * and also safely handle when the document is not found because the final list is built based on partial data.
   * @param {string} leadId - The lead id
   * @returns {Promise<LeadConversation|null>} The conversation document, or null if not found
   */
  const getConversationDoc = useCallback(async (leadId) => {
    const cachedDoc = conversationByLeadIdRef.current.get(leadId);
    if (cachedDoc) {
      return cachedDoc;
    }
    return LeadConversation.getById(leadId);
  }, []);

  /**
   * Handle the claim change for the active lead.
   * We use this to unclaim the previous conversation (only if we own the claim)
   * and claim the new active lead.
   * @returns {void}
   */
  useEffect(() => {
    const prevLeadId = prevActiveLeadIdRef.current;
    prevActiveLeadIdRef.current = activeLeadId;
    let isCurrentRequest = true;

    const handleClaimChange = async () => {
      // Always unclaim previous lead (transaction-safe no-op if IG was never claimed)
      if (prevLeadId && prevLeadId !== activeLeadId) {
        const prevDoc = await getConversationDoc(prevLeadId);
        if (prevDoc?.exists) {
          await prevDoc.unclaim(authUserId);
        }
      }

      if (!activeLeadId) {
        return;
      }

      // Skip claiming for IG conversations — only text/email conversations are claimed
      if (activeChannel === ConversationChannel.INSTAGRAM) {
        return;
      }

      // Get the conversation doc for the new active lead
      const leadConvDoc = await getConversationDoc(activeLeadId);

      if (!isCurrentRequest || !isComponentMountedRef.current) return;

      if (!leadConvDoc) {
        return;
      }

      // Attempt to claim
      const claimResult = await leadConvDoc.claim(authUserId, authUserName);

      if (!isCurrentRequest || !isComponentMountedRef.current) return;

      if (claimResult && claimResult.claimedBy && claimResult.claimedBy !== authUserId) {
        // Someone else holds a valid claim — surface takeover modal
        setIsPendingTakeover(true);
        setPendingClaimedByUsername(claimResult.claimedByUsername || claimResult.claimedBy);
        setPendingTakeoverLeadId(activeLeadId);
        // Revert active lead to the previous selection
        setActiveLeadId(prevLeadId || '');
      }
    };

    handleClaimChange();

    return () => { isCurrentRequest = false; };
  }, [activeLeadId, activeChannel, authUserId, authUserName, isComponentMountedRef, getConversationDoc]);

  /**
   * Unclaim the previous conversation (only if we own the claim)
   * when the component unmounts.
   * @returns {void}
   */
  useEffect(() => async () => {
    const currentLeadId = prevActiveLeadIdRef.current;
    if (currentLeadId) {
      const leadConvDoc = await getConversationDoc(currentLeadId);
      if (leadConvDoc?.exists) {
        leadConvDoc.unclaim(authUserId);
      }
    }
  }, [authUserId, getConversationDoc]);

  useEffect(() => {
    const disposer = autorun(() => {
      const conversationDocMap = new Map();

      conversationCollectionList.forEach((collection) => {
        collection.docs.forEach((doc) => {
          if (!conversationDocMap.has(doc.id)) {
            conversationDocMap.set(doc.id, doc);
          }
        });
      });

      conversationByLeadIdRef.current = conversationDocMap;
      const docList = Array.from(conversationDocMap.values());
      setConversationList(docList);
    });

    return () => disposer();
  }, [
    conversationCollectionList,
    activeLeadId,
  ]);

  useEffect(() => {
    const disposer = autorun(() => {
      const igConversationDocMap = new Map();
      igConversationCollectionList.forEach((collection) => {
        collection.docs.forEach((doc) => {
          if (!igConversationDocMap.has(doc.id)) {
            igConversationDocMap.set(doc.id, doc);
          }
        });
      });
      setIGConversationList(Array.from(igConversationDocMap.values()));
    });
    return () => disposer();
  }, [igConversationCollectionList]);

  /**
   * Filter already-fetched conversations client-side by query string.
   * When the user needs more results, they can load more pages via loadMoreConversations,
   * which will automatically expand the results as conversationList grows.
   */
  const searchResults = useMemo(() => {
    if (!searchQuery) return null;
    const lowerQuery = searchQuery.toLowerCase();
    return conversationList.filter(
      (doc) => SEARCH_FIELDS.some((field) => (doc[field] || '').toLowerCase().includes(lowerQuery)),
    );
  }, [searchQuery, conversationList]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query.trim());
  }, []);

  const loadMoreConversations = useCallback(async () => {
    setIsLoadingConversations(true);

    const lastCollection = conversationCollectionList[conversationCollectionList.length - 1];
    const lastDoc = lastCollection.docs[lastCollection.docs.length - 1];

    const isArchived = selectedBucket === ConversationBucket.ARCHIVED;
    const nextCollection = LeadConversation.getConversations(isArchived, {
      startAfterDoc: lastDoc,
      limit: CONVERSATIONS_PAGE_SIZE,
    });

    await nextCollection.fetch();

    if (isComponentMountedRef.current) {
      if (nextCollection.docs.length < CONVERSATIONS_PAGE_SIZE) {
        setHasMoreConversations(false);
      }
      setIsLoadingConversations(false);
      setConversationCollectionList((prev) => ([...prev, nextCollection]));
    }
  }, [
    conversationCollectionList,
    selectedBucket,
    isComponentMountedRef,
  ]);

  const setActiveConversation = useCallback((leadId, channel) => {
    setActiveLeadId(leadId);
    setActiveChannel(channel);
  }, []);

  const loadMoreIGConversations = useCallback(async () => {
    setIsLoadingIGConversations(true);

    const lastCollection = igConversationCollectionList[igConversationCollectionList.length - 1];
    const lastDoc = lastCollection.docs[lastCollection.docs.length - 1];

    const igIsArchived = selectedBucket === ConversationBucket.ARCHIVED;
    const nextCollection = LeadConversation.getIGConversations(igIsArchived, {
      startAfterDoc: lastDoc,
      limit: CONVERSATIONS_PAGE_SIZE,
    });

    await nextCollection.fetch();

    if (isComponentMountedRef.current) {
      if (nextCollection.docs.length < CONVERSATIONS_PAGE_SIZE) {
        setHasMoreIGConversations(false);
      }
      setIsLoadingIGConversations(false);
      setIGConversationCollectionList((prev) => [...prev, nextCollection]);
    }
  }, [igConversationCollectionList, selectedBucket, isComponentMountedRef]);

  const confirmTakeover = useCallback(async () => {
    const leadIdToActivate = pendingTakeoverLeadId;
    const leadConvDoc = await LeadConversation.getById(leadIdToActivate);
    if (leadConvDoc) {
      await leadConvDoc.reclaim(authUserId, authUserName);
    }
    if (!isComponentMountedRef.current) return;
    setIsPendingTakeover(false);
    setPendingClaimedByUsername('');
    setPendingTakeoverLeadId('');
    setActiveLeadId(leadIdToActivate);
  }, [pendingTakeoverLeadId, authUserId, authUserName, isComponentMountedRef]);

  const cancelTakeover = useCallback(() => {
    setIsPendingTakeover(false);
    setPendingClaimedByUsername('');
    setPendingTakeoverLeadId('');
  }, []);

  const conversationPagesLoaded = conversationCollectionList.length;
  const igConversationPagesLoaded = igConversationCollectionList.length;

  const context = useMemo(() => ({
    isReady,
    conversationList,
    isLoadingConversations,
    hasMoreConversations,
    loadMoreConversations,
    selectedBucket,
    setSelectedBucket,
    activeLeadId,
    setActiveLeadId,
    activeLeadDoc,
    isLoading,
    isPendingTakeover,
    pendingClaimedByUsername,
    confirmTakeover,
    cancelTakeover,
    searchResults,
    handleSearch,
    selectedChannel,
    setSelectedChannel,
    activeChannel,
    setActiveConversation,
    igConversationList,
    isLoadingIGConversations,
    hasMoreIGConversations,
    loadMoreIGConversations,
    conversationPagesLoaded,
    igConversationPagesLoaded,
  }), [
    isReady,
    conversationList,
    isLoadingConversations,
    hasMoreConversations,
    loadMoreConversations,
    selectedBucket,
    activeLeadId,
    activeLeadDoc,
    isLoading,
    isPendingTakeover,
    pendingClaimedByUsername,
    confirmTakeover,
    cancelTakeover,
    searchResults,
    handleSearch,
    selectedChannel,
    activeChannel,
    setActiveConversation,
    igConversationList,
    isLoadingIGConversations,
    hasMoreIGConversations,
    loadMoreIGConversations,
    conversationPagesLoaded,
    igConversationPagesLoaded,
  ]);

  return (
    <LeadConversationsContext.Provider value={context}>
      {children}
    </LeadConversationsContext.Provider>
  );
};

LeadConversationsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(LeadConversationsContextProvider);
